import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { FileText, ShieldCheck, Upload } from 'lucide-react';

type UploadContext =
  | {
      valid: true;
      prenom: string | null;
      etablissement_nom: string | null;
      already_uploaded: boolean;
      statut: string | null;
      expires_at: string | null;
    }
  | {
      valid: false;
      reason: 'invalid_token' | 'expired_token';
      prenom?: string | null;
      etablissement_nom?: string | null;
    };

export default function AttestationUpload() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();

  const [context, setContext] = useState<UploadContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [file, setFile] = useState<File | null>(null);

  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();
    loadContext(token, controller.signal)
      .then(setContext)
      .catch(() => {
        setContext({ valid: false, reason: 'invalid_token' });
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [token]);

  const handleSubmit = async () => {
    if (!token || !file) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('token', token);
      formData.append('file', file);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/attestation-upload`, {
        method: 'POST',
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || "Impossible d'envoyer l'attestation");
      }

      setCompleted(true);
      toast({
        title: 'Document transmis',
        description: "Votre attestation a bien été envoyée et va être vérifiée automatiquement.",
      });
    } catch (error: any) {
      toast({
        title: 'Erreur',
        description: error.message || "Impossible d'envoyer votre attestation",
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#f8fbff_0%,#eef6f6_45%,#f8fafc_100%)] px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 lg:flex-row lg:items-start">
        <section className="w-full lg:max-w-sm">
          <div className="rounded-3xl border border-white/70 bg-white/85 p-5 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur sm:p-6">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#40E0D0]/15 text-[#0f766e]">
              <ShieldCheck className="h-6 w-6" />
            </div>

            <h1 className="mt-4 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
              Déposer votre attestation
            </h1>

            <p className="mt-3 text-sm leading-6 text-slate-600 sm:text-base">
              Envoyez votre attestation d&apos;honorabilité directement depuis cette page. Le document sera analysé automatiquement dès sa réception.
            </p>

            <div className="mt-6 grid gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Formats acceptés</p>
                <p className="mt-1 text-sm text-slate-600">PDF, JPG ou PNG jusqu&apos;à 10 MB.</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-sm font-medium text-slate-900">Traitement</p>
                <p className="mt-1 text-sm text-slate-600">Après l&apos;envoi, votre document passe en vérification automatiquement.</p>
              </div>
            </div>
          </div>
        </section>

        <Card className="w-full border-white/70 bg-white/90 shadow-[0_18px_60px_rgba(15,23,42,0.08)]">
          <CardHeader className="space-y-3 p-5 sm:p-6">
            <CardTitle className="text-xl font-semibold text-slate-900 sm:text-2xl">
              Téléversement sécurisé
            </CardTitle>
            <CardDescription className="text-sm leading-6 text-slate-600 sm:text-base">
              Téléversez directement votre attestation d&apos;honorabilité au format PDF ou image.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-5 p-5 pt-0 sm:space-y-6 sm:p-6 sm:pt-0">
            {loading && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                Chargement du lien sécurisé...
              </div>
            )}

            {!loading && context?.valid === false && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
                {context.reason === 'expired_token'
                  ? 'Ce lien de dépôt a expiré. Merci de demander un nouveau lien à votre responsable.'
                  : 'Ce lien de dépôt est invalide.'}
              </div>
            )}

            {!loading && context?.valid && !completed && (
              <>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700 sm:p-5">
                  <p>
                    Bonjour {context.prenom ? <strong>{context.prenom}</strong> : 'à vous'}
                    {context.etablissement_nom ? `, pour ${context.etablissement_nom}` : ''}.
                  </p>
                  <p className="mt-2">
                    Si vous avez déjà votre attestation, vous pouvez la déposer ici directement. Dès réception, elle sera analysée automatiquement.
                  </p>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-slate-100 p-2 text-slate-700">
                        <FileText className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">Document lisible</p>
                        <p className="mt-1 text-sm text-slate-600">
                          Préférez le PDF original si vous l&apos;avez déjà téléchargé.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-slate-200 p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 rounded-xl bg-slate-100 p-2 text-slate-700">
                        <Upload className="h-4 w-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">Lien à usage simple</p>
                        <p className="mt-1 text-sm text-slate-600">
                          Le lien est sécurisé et le token d&apos;upload est invalidé après envoi.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50/80 p-4 sm:p-6">
                  <label className="block text-sm font-medium text-slate-800">
                    Votre document
                  </label>

                  <label
                    htmlFor="attestation-file"
                    className="mt-3 flex cursor-pointer flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-8 text-center transition hover:border-[#40E0D0] hover:bg-[#40E0D0]/5 sm:px-6"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#40E0D0]/15 text-[#0f766e]">
                      <Upload className="h-5 w-5" />
                    </div>
                    <p className="mt-4 text-sm font-medium text-slate-900">
                      {file ? file.name : 'Touchez ici pour choisir votre fichier'}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                      PDF, JPG ou PNG. Taille maximale : 10 MB.
                    </p>
                  </label>

                  <input
                    id="attestation-file"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(event) => setFile(event.target.files?.[0] ?? null)}
                    className="sr-only"
                  />

                  {file && (
                    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                      Fichier sélectionné : <strong>{file.name}</strong>
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleSubmit}
                  disabled={!file || submitting}
                  className="h-12 w-full rounded-xl text-sm font-medium sm:h-11"
                >
                  {submitting ? "Envoi en cours..." : "Envoyer mon attestation"}
                </Button>
              </>
            )}

            {completed && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm leading-6 text-emerald-900 sm:p-5">
                Votre attestation a bien été reçue. Elle va maintenant être contrôlée automatiquement.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

async function loadContext(token: string, signal: AbortSignal): Promise<UploadContext> {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/attestation-upload?token=${encodeURIComponent(token)}`,
    {
      method: 'GET',
      headers: {
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      signal,
    }
  );

  return await response.json();
}
